/**
 * Utility functions for handling fonts in the website builder (client-side)
 */

/**
 * Loads a font in the document head
 * @param {Object} font - Font object with font properties
 * @param {String} dataAttribute - Optional data attribute to add to the link
 * @returns {HTMLLinkElement|null} The created link element or null if invalid
 */
export const loadFontInHead = (font, dataAttribute = 'data-custom-font') => {
  if (!font || !font.googleUrl) {
    console.warn('Invalid font object provided to loadFontInHead');
    return null;
  }
  
  // Check if this font is already loaded
  const existingLink = document.querySelector(`link[href="${font.googleUrl}"]`);
  if (existingLink) {
    return existingLink;
  }
  
  // Create and add new link element
  const link = document.createElement('link');
  link.href = font.googleUrl;
  link.rel = 'stylesheet';
  
  if (dataAttribute) {
    link.setAttribute(dataAttribute, 'true');
  }
  
  document.head.appendChild(link);
  console.log(`Loaded font: ${font.family} (${font.name})`);
  return link;
};

/**
 * Loads multiple fonts into the document head
 * @param {Array} fonts - Array of font objects or font references
 * @param {String} dataAttribute - Optional data attribute to add to the link
 */
export const loadFontsInHead = (fonts, dataAttribute = 'data-custom-font') => {
  if (!fonts || !Array.isArray(fonts) || fonts.length === 0) {
    return;
  }
  
  fonts.forEach(fontRef => {
    // Handle both direct font objects and font references from site.fonts
    const font = fontRef.font || fontRef;
    if (font && font.googleUrl) {
      loadFontInHead(font, dataAttribute);
    }
  });
};

/**
 * Removes all font links with a specific data attribute from the document head
 * @param {String} dataAttribute - The data attribute to look for
 */
export const removeCustomFonts = (dataAttribute = 'data-custom-font') => {
  const fontLinks = document.querySelectorAll(`link[${dataAttribute}]`);
  fontLinks.forEach(link => link.remove());
};

/**
 * Generates CSS variables for fonts
 * @param {Array} fonts - Array of font objects with font reference and properties
 * @returns {String} CSS for font variables
 */
export const generateFontCSS = (fonts) => {
  if (!fonts || !Array.isArray(fonts) || fonts.length === 0) {
    return '';
  }
  
  // Find the default font
  const defaultFont = fonts.find(f => f.isDefault && f.font);
  
  // Generate CSS variables for all fonts
  const fontVariables = fonts
    .filter(f => f.font && f.font.family)
    .map(f => {
      const font = f.font;
      const family = font.family.toLowerCase().replace(/\s+/g, '-');
      return `  --font-${family}: "${font.family}", ${font.category || 'sans-serif'};`;
    })
    .join('\n');
  
  // Generate body font-family if there's a default font
  let bodyFont = '';
  if (defaultFont && defaultFont.font) {
    const family = defaultFont.font.family.toLowerCase().replace(/\s+/g, '-');
    bodyFont = `\n\nbody {\n  font-family: var(--font-${family});\n}`;
  }
  
  return `:root {\n${fontVariables}\n}${bodyFont}`;
}; 